import type { SassmallApiResponse } from "./api/client";
import { st } from "./i18n";

export function toLegacySuccess<T>(response: SassmallApiResponse<T>, data: any = response?.data) {
  const success = Number(response?.code) === 200 || response?.success === true;
  return {
    ...response,
    code: response?.code ?? (success ? 200 : 500),
    data,
    msg: String(response?.message || response?.msg || (success ? st("操作成功") : st("操作失败"))),
    success,
  };
}

export function normalizeLegacyUser(data: Record<string, any> = {}, token = "") {
  const id = String(data.id || data.customerId || data.userId || "");
  const account = String(data.account || data.username || data.email || data.mobile || "");
  const name = String(data.nickname || data.nickName || data.realName || data.name || account);
  const memberCode = String(data.memberCode || data.customerCode || data.code || id);
  return {
    ...data,
    id,
    account,
    name,
    realName: String(data.realName || name),
    avatar: String(data.avatar || data.avatarUrl || ""),
    memberCode,
    token,
    roles: Array.isArray(data.roles) && data.roles.length
      ? data.roles
      : [{ id: "SASSMALL_CUSTOMER", code: "TMS_MEMBER", name: st("商城会员") }],
    mallUserInfo: {
      ...data,
      id,
      userId: id,
      memberCode,
      nickName: name,
      realName: String(data.realName || name),
      avatarUrl: String(data.avatarUrl || data.avatar || ""),
    },
    customerInfo: {
      ...data,
      id,
      userId: id,
      memberCode,
      nickname: name,
    },
  };
}

export function normalizeLegacyFund(data: Record<string, any> = {}) {
  const available = Number(
    data.totalAvailableBalance
      ?? data.availableBalance
      ?? data.balance
      ?? data.fundBalance
      ?? 0,
  );
  return {
    ...data,
    fundBalance: available,
    balance: available,
    integral: Number(data.pointsBalance ?? data.integral ?? data.points ?? 0),
    commission: Number(data.commissionBalance ?? data.commission ?? 0),
  };
}

export function normalizeLegacyWarehouse(item: Record<string, any> = {}) {
  const id = String(item.id || item.warehouseId || item.code || item.warehouseCode || "");
  const warehouseName = String(item.name || item.warehouseName || item.wareHouseName || "");
  const contactName = String(item.contactName || item.receiver || item.person || "");
  const contactMobile = String(item.contactMobile || item.mobile || item.phone || "");
  const country = String(item.countryName || item.country || "");
  const province = String(item.provinceName || item.province || item.state || "");
  const city = String(item.cityName || item.city || "");
  const district = String(item.districtName || item.district || item.area || "");
  const address = String(item.fullAddress || item.address || item.detailAddress || "");
  return {
    ...item,
    id,
    warehouseId: id,
    warehouseCode: String(item.warehouseCode || item.code || id),
    warehouseName,
    wareHouseName: warehouseName,
    country,
    province,
    city,
    district,
    address,
    concatInfos: [{
      address,
      city,
      contactMobile,
      contactName,
      country,
      district,
      province,
    }],
  };
}

export function sassmallPlatformToLegacyOrigin(platform: any) {
  const normalized = String(platform || "").toUpperCase();
  if (normalized.includes("1688") || normalized.includes("ALIBABA")) return 2;
  if (normalized.includes("WEIDIAN")) return 3;
  return 1;
}

export function normalizeLegacyCartItem(item: Record<string, any> = {}) {
  const id = String(item.id || "");
  const platform = String(item.platform || "TAOBAO").toUpperCase();
  const price = Number(item.price ?? item.priceSnapshot ?? item.saleUnitPrice ?? 0);
  return {
    ...item,
    id,
    cartItemId: id,
    itemId: String(item.itemId || id),
    itemSkuId: String(item.skuId || item.itemSkuId || id),
    specId: String(item.skuId || item.specId || item.itemSkuId || id),
    image: String(item.imageUrl || item.image || item.picUrl || ""),
    iconUrl: String(item.imageUrl || item.iconUrl || item.image || ""),
    price: Math.round(price * 100),
    priceAmount: price,
    quantity: Math.max(1, Number(item.quantity || 1)),
    shopId: String(item.shopId || `${platform}-default`),
    shopNick: String(item.shopName || item.shopNick || platform),
    specsInfo: String(item.skuText || item.specsInfo || ""),
    specsInfoText: String(item.skuText || item.specsInfoText || item.specsInfo || ""),
    title: String(item.title || item.itemTitle || ""),
    origin: sassmallPlatformToLegacyOrigin(platform),
    platform,
  };
}

export function readSassmallRecords(response: any) {
  const data = response?.data || {};
  return Array.isArray(data.records)
    ? data.records
    : Array.isArray(data.dataList)
      ? data.dataList
      : Array.isArray(data.list)
        ? data.list
        : Array.isArray(data)
          ? data
          : [];
}

export function amountToFen(value: any) {
  return Math.round(Number(value || 0) * 100);
}

const SASSMALL_PLATFORM_TO_LEGACY_SOURCE: Record<string, number> = {
  TAOBAO: 1,
  TB: 1,
  TMALL: 1,
  JD: 2,
  JINGDONG: 2,
  PDD: 3,
  PINDUODUO: 3,
  "1688": 4,
  ALI_1688: 4,
  ALIBABA: 4,
  XHS: 5,
  XIAOHONGSHU: 5,
  XIANYU: 6,
  WEIDIAN: 7,
  DOUYIN: 9,
  VIPSHOP: 10,
  DEWU: 11,
  SUNING: 12,
};

export function sassmallPlatformToLegacyOrderSource(platform: any) {
  return SASSMALL_PLATFORM_TO_LEGACY_SOURCE[String(platform || "").trim().toUpperCase()] || 8;
}

export function sassmallStatusToLegacyDisplayStatus(status: any) {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "WAIT_PAY") return "WAITING_PAY";
  if (["WAIT_PURCHASE", "PURCHASING", "PURCHASED"].includes(normalized)) return "PURCHASED";
  if (normalized === "WAIT_INBOUND") return "WAITING_INBOUND";
  if (["INBOUNDED", "COMPLETED"].includes(normalized)) return "COMPLETED";
  if (normalized === "CANCELLED") return "CANCELLED";
  if (normalized === "CLOSED") return "CLOSED";
  return normalized || "WAITING_PAY";
}

function normalizeLegacyOrderDate(value: any) {
  return String(value || "").trim().replace("T", " ");
}

export function normalizeLegacyOrderItem(
  item: Record<string, any> = {},
  order: Record<string, any> = {},
) {
  const sourceSaleOrderItemId = typeof item.id === "string" && /^[1-9]\d*$/.test(item.id.trim())
    ? item.id.trim()
    : "";
  const saleOrderStatus = String(order.saleOrderStatus || order.status || "").toUpperCase();
  const displayStatus = sassmallStatusToLegacyDisplayStatus(saleOrderStatus);
  const unitPrice = amountToFen(item.unitPrice ?? item.saleUnitPrice ?? item.price);
  const itemAmount = amountToFen(
    item.amount ?? Number(item.unitPrice ?? item.saleUnitPrice ?? item.price ?? 0) * Number(item.quantity || 1),
  );
  return {
    ...item,
    id: String(item.id || item.detailNo || item.itemId || ""),
    sourceSaleOrderItemId,
    detailNo: String(item.detailNo || item.id || ""),
    image: String(item.imageUrl || item.image || item.picUrl || ""),
    itemId: String(item.itemId || ""),
    mpId: String(item.itemId || ""),
    skuId: String(item.skuId || ""),
    mpSkuId: String(item.skuId || ""),
    spec: String(item.skuText || item.spec || ""),
    price: unitPrice,
    purchaseMoney: itemAmount,
    quantity: Math.max(1, Number(item.quantity || 1)),
    orderStatus: saleOrderStatus === "WAIT_PAY" ? "WAIT_BUYER_P" : saleOrderStatus,
    purchaseStatus: String(item.purchaseStatus || saleOrderStatus).toUpperCase(),
    displayStatus,
    displayStatusName: String(item.purchaseStatusName || order.saleOrderStatusName || order.statusName || ""),
    showStatusName: String(item.purchaseStatusName || order.saleOrderStatusName || order.statusName || ""),
    orderRemark: String(item.remark || ""),
    logisticsBillNo: String(item.logisticsNo || ""),
    logisticsNo: String(item.logisticsNo || ""),
    operations: Array.isArray(item.operations) ? item.operations : [],
    rawUrl: String(item.rawUrl || item.normalizedUrl || item.sourceUrl || item.productUrl || ""),
  };
}

export function normalizeLegacyOrder(order: Record<string, any> = {}) {
  const id = String(order.id || order.saleOrderId || "");
  const orderNo = String(order.saleOrderNo || order.orderNo || id);
  const saleOrderStatus = String(order.saleOrderStatus || order.status || "").toUpperCase();
  const displayStatus = sassmallStatusToLegacyDisplayStatus(saleOrderStatus);
  const items = Array.isArray(order.items) ? order.items : [];
  const detailResults = items.map(item => normalizeLegacyOrderItem(item, order));
  const goodsAmount = amountToFen(
    order.goodsAmount
      ?? items.reduce((total, item) => total + Number(item.amount || 0), 0),
  );
  const shippingFee = amountToFen(order.domesticFreightAmount ?? order.shippingFee);
  const serviceFee = amountToFen(order.serviceFeeAmount ?? order.commissionFee);
  const discountAmount = amountToFen(order.discountAmount ?? order.couponFee);
  const payableAmount = amountToFen(order.payableAmount ?? order.totalAmount);
  const effectivePayableAmount = String(order.orderType || "").toUpperCase() === "TRANSFER" && payableAmount === 0
    ? Math.max(goodsAmount + shippingFee + serviceFee - discountAmount, 0)
    : payableAmount;
  const paidAmount = amountToFen(order.paidAmount ?? order.customerPaidAmount);
  const orderCreateTime = normalizeLegacyOrderDate(order.createdAt || order.orderCreateTime);
  const payTime = normalizeLegacyOrderDate(order.paymentTime || order.payTime);
  const orderSource = sassmallPlatformToLegacyOrderSource(order.platform || items[0]?.platform);
  const headerOperations = Array.isArray(order.headerOperations) ? order.headerOperations : [];
  return {
    ...order,
    __sassmall: true,
    id,
    saleOrderId: id,
    saleOrderNo: orderNo,
    orderId: orderNo,
    purchaseId: id,
    outerOrderId: String(items[0]?.purchaseOrderId || orderNo),
    orderSource,
    sourceMarket: String(order.platform || items[0]?.platform || ""),
    sassmallStatus: saleOrderStatus,
    orderCreateTime,
    createTime: orderCreateTime,
    payTime,
    status: displayStatus,
    statusName: String(order.saleOrderStatusName || order.statusName || saleOrderStatus),
    displayStatus,
    headerDisplayStatus: displayStatus,
    displayStatusName: String(order.saleOrderStatusName || order.statusName || saleOrderStatus),
    orderStatus: saleOrderStatus === "WAIT_PAY" ? "WAIT_BUYER_P" : saleOrderStatus,
    custPayMoney: effectivePayableAmount,
    payMoney: paidAmount || effectivePayableAmount,
    purchaseMoney: goodsAmount + shippingFee,
    sassmallGoodsAmount: goodsAmount,
    sassmallOrderAmount: effectivePayableAmount,
    sassmallPaidAmount: paidAmount,
    sassmallDomesticFreightAmount: shippingFee,
    sassmallServiceFeeAmount: serviceFee,
    shippingFee,
    commissionFee: serviceFee,
    couponFee: discountAmount,
    purchasePromotionMoney: discountAmount,
    orderRemark: String(order.remark || ""),
    detailResults,
    operations: headerOperations,
    headerOperations,
    canPay: headerOperations.includes("PAY_NOW"),
    canCancel: headerOperations.includes("CANCEL_ORDER"),
  };
}

export function normalizeLegacyOrderStatusCounts(summary: Record<string, any> = {}) {
  return {
    ALL: Number(summary.allCount || 0),
    WAIT_PAY: Number(summary.waitPayCount || 0),
    PURCHASING: Number(summary.waitPurchaseCount || 0) + Number(summary.purchasingCount || 0),
    PURCHASED: Number(summary.purchasedCount || 0),
    WAIT_INBOUND: Number(summary.waitInboundCount || 0),
    INBOUNDED: Number(summary.inboundedCount || 0),
    COMPLETED: Number(summary.completedCount || 0),
    CLOSED: Number(summary.cancelCount || 0) + Number(summary.closedCount || 0),
  };
}

export function normalizeLegacyPreviewItem(item: Record<string, any> = {}) {
  return {
    ...normalizeLegacyCartItem(item),
    price: amountToFen(item.saleUnitPrice ?? item.price ?? item.priceSnapshot),
  };
}

export function extractSassmallCreatedOrders(data: Record<string, any> = {}) {
  const saleOrders = Array.isArray(data.saleOrders) ? data.saleOrders : [];
  return {
    saleOrderIds: saleOrders.map(item => String(item.id || "")).filter(Boolean),
    orderNos: saleOrders.map(item => String(item.saleOrderNo || item.orderNo || "")).filter(Boolean),
  };
}
